import { cx } from "@/lib/cx.js";
import { nextSort } from "@/lib/sort.js";

/*
 * Table primitives over the .th / .td classes in index.css.
 *
 * <TableWrap minWidth="1080px"> is what keeps a wide table scrolling inside its
 * own container instead of pushing the page sideways on a phone. Every data table
 * in the markup needed it, so it is part of the component rather than something
 * each page remembers.
 */

export function TableWrap({ minWidth, className, children }) {
    return (
        <div className={cx("overflow-x-auto", className)}>
            <table className="w-full" style={minWidth ? { minWidth } : undefined}>
                {children}
            </table>
        </div>
    );
}

export function Th({ first, last, className, children, ...rest }) {
    return (
        <th className={cx("th", first && "pl-4.5", last && "pr-4.5", className)} {...rest}>
            {children}
        </th>
    );
}

export function Td({ first, last, className, children, ...rest }) {
    return (
        <td className={cx("td", first && "pl-4.5", last && "pr-4.5", className)} {...rest}>
            {children}
        </td>
    );
}

export function Tr({ onClick, className, children, ...rest }) {
    return (
        <tr
            onClick={onClick}
            className={cx(
                "transition hover:bg-slate-50 dark:hover:bg-slate-800/50",
                onClick && "cursor-pointer",
                className,
            )}
            {...rest}
        >
            {children}
        </tr>
    );
}

/**
 * Sortable header cell.
 *
 *   <SortableTh field="name" sort={sort} onSort={setSort}>User</SortableTh>
 *
 * `sort` is { by, dir }. Clicking a different column sorts it; clicking the
 * active one flips direction. aria-sort is set so assistive tech announces it —
 * the markup's caret was visual only.
 */
export function SortableTh({ field, sort, onSort, first, last, className, children }) {
    const active = sort?.by === field;
    const dir = active ? sort.dir : null;

    const toggle = () => onSort?.(nextSort(sort, field));

    return (
        <th
            scope="col"
            aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
            className={cx("th cursor-pointer select-none", first && "pl-4.5", last && "pr-4.5", className)}
            onClick={toggle}
        >
            <span className="inline-flex items-center gap-1.5">
                {children}
                <span
                    aria-hidden="true"
                    className={active ? "text-brand-600 dark:text-brand-400" : "opacity-40"}
                >
                    {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
                </span>
            </span>
        </th>
    );
}
